import React, { useEffect, useState } from "react";
import styles from "../User/Auth.module.css";
import AffiliateShareModal from "./AffiliateShareModal";
import api from "../../api/axois";

// Circular countdown component
const CircularCountdown = ({ daysUntilPayment, isOverdue, payoutFrequency }) => {
  const maxDays = payoutFrequency === 'weekly' ? 7 : 30;
  const percentage = isOverdue ? 0 : Math.max(0, (daysUntilPayment / maxDays) * 100);
  const radius = 30;
  const strokeWidth = 6;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  let color = '#28a745'; // green
  let message = `${Math.floor(daysUntilPayment)} days until payment`;
  
  if (isOverdue) {
    color = '#dc3545';
    message = 'PAYMENT OVERDUE!';
  } else if (daysUntilPayment === 0) {
    color = '#ffc107';
    message = 'Payment due today';
  } else if (daysUntilPayment === 1) {
    color = '#fd7e14';
    message = 'Payment due tomorrow';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width={radius * 2} height={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          stroke="#e9ecef"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <text
          x={radius}
          y={radius + 4}
          textAnchor="middle"
          fontSize="12"
          fontWeight="bold"
          fill={color}
          transform={`rotate(90 ${radius} ${radius})`}
        >
          {isOverdue ? '!' : Math.floor(daysUntilPayment)}
        </text>
      </svg>
      <div style={{ fontWeight: 'bold', color }}>
        {message}
      </div>
    </div>
  );
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '0';
  const num = Number(val);
  if (isNaN(num)) return '0';
  return num.toLocaleString();
};

const AffiliateDashboard = () => {
  const [affiliate, setAffiliate] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);

  const loadDashboard = () => {
    setLoading(true);
    api.get("affiliate/dashboard")
      .then(res => {
        const data = res.data || {};
        const a = data.affiliate || {};
        if (a.total_commission == null) {
          const pending = Number(a.pending_commission || 0);
          const paid = Number(a.paid_commission || 0);
          a.total_commission = pending + paid;
        }
        setAffiliate(a);
        setBookings(Array.isArray(data.bookings) ? data.bookings : []);
        setError("");
      })
      .catch(e => {
        console.error("Affiliate dashboard error:", e);
        setError(e.response?.data?.message || e.message || "Error loading dashboard");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const getPaymentCountdownDisplay = (affiliate) => {
    if (!affiliate) return <div>Loading...</div>;
    
    return (
      <CircularCountdown 
        daysUntilPayment={affiliate.days_until_payment || 0}
        isOverdue={affiliate.is_payment_overdue || false}
        payoutFrequency={affiliate.payout_frequency || 'monthly'}
      />
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.box}>
        <h2 className={styles.heading} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Affiliate Dashboard</span>
          <button
            className={styles.button}
            style={{ padding: '6px 14px', fontSize: 14, width: "auto" }}
            onClick={loadDashboard}
            disabled={loading}
          >{loading ? 'Refreshing...' : 'Refresh'}</button>
        </h2>
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : (
          <>
            <div className={styles.note}>
              <strong>Affiliate Code:</strong> {affiliate?.affiliate_code}
              <button className={styles.button} style={{ marginLeft: 12, fontSize: 14, padding: "8px 16px", width: "auto" }} onClick={() => setShowShareModal(true)}>
                Share/Copy Link
              </button><br />
              <strong>Total Commission:</strong> ₦{formatCurrency(affiliate?.total_commission)}
              <br />
              {affiliate?.pending_commission !== undefined && (
                <><strong>Pending Commission:</strong> ₦{formatCurrency(affiliate?.pending_commission)}<br /></>
              )}
              {affiliate?.paid_commission !== undefined && (
                <><strong>Paid Commission:</strong> ₦{formatCurrency(affiliate?.paid_commission)}<br /></>
              )}
              <strong>Payout Frequency:</strong> {affiliate?.payout_frequency}
              <br />
              <div style={{ marginTop: 16 }}>
                <strong>Payment Status:</strong>
                <div style={{ marginTop: 8 }}>
                  {getPaymentCountdownDisplay(affiliate)}
                </div>
              </div>
            </div>

            <div style={{
              marginTop: 16,
              background: "#f0f8ff",
              border: "2px solid #007bff",
              borderRadius: 8,
              padding: 16,
              textAlign: 'center'
            }}>
              <h4 style={{ margin: 0, marginBottom: 8, color: '#007bff' }}>Automatic Payment Schedule</h4>
              <p style={{ margin: 0, color: '#495057' }}>
                Your commission will be automatically paid every <strong>{affiliate?.payout_frequency}</strong> 
                {affiliate?.pending_commission > 0 && (
                  <>
                    <br />
                    <strong>Amount to be paid:</strong> ₦{formatCurrency(affiliate?.pending_commission)}
                  </>
                )}
              </p>
            </div>

            <AffiliateShareModal
              isOpen={showShareModal}
              onClose={() => setShowShareModal(false)}
              affiliateCode={affiliate?.affiliate_code}
            />
            <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Bookings Attributed to You</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Archivo, sans-serif" }}>
                <thead>
                  <tr style={{ background: "#f0f0f0" }}>
                    <th style={{ padding: "10px", border: "1px solid #ccc" }}>Booking ID</th>
                  
                    <th style={{ padding: "10px", border: "1px solid #ccc" }}>Booking Date</th>
                    <th style={{ padding: "10px", border: "1px solid #ccc" }}>Booking Value (₦)</th>
                    <th style={{ padding: "10px", border: "1px solid #ccc" }}>Commission (₦)</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", padding: "20px" }}>No bookings yet.</td>
                    </tr>
                  ) : (
                    bookings.map((booking) => (
                      <tr key={booking.id}>
                        <td style={{ padding: "10px", border: "1px solid #ccc" }}>{booking.id}</td>
                        <td style={{ padding: "10px", border: "1px solid #ccc" }}>{booking.date}</td>
                        <td style={{ padding: "10px", border: "1px solid #ccc" }}>₦{formatCurrency(booking.booking_value)}</td>
                        <td style={{ padding: "10px", border: "1px solid #ccc" }}>₦{formatCurrency(booking.commission)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboard;